import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Send } from 'lucide-react';
import { useSettings } from '../../App';
import { publicAPI } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useMember } from '../../lib/memberAuth';
import { BACKEND_URL } from '../../lib/config';
import { useT } from '../../lib/i18n';
import { resolveActivePersonality, scopePagesForPersonality } from '../../lib/pbPersonality';
import { useSocialCatalog, resolveKey, SocialIcon } from '../../lib/socialCatalog';

// Categories exclusive to the Personal Brand mini-sites — hidden from other themes.
const PB_CATS = new Set(['business', 'lifestyle', 'personal']);

function useFooterData() {
  const settings = useSettings();
  const { user } = useAuth();
  const { member } = useMember();
  const location = useLocation();
  const isPB = (settings.active_theme || 'default') === 'personalbrand';
  const [allPages, setAllPages] = React.useState([]);
  // Only ACTIVE networks appear on the public site (SOCIAL_LINKS_KMS_SYNC_PLAN).
  const socialLinks = (settings.social_links || []).filter(l => l.active !== false && (l.url || '').trim());

  React.useEffect(() => {
    // Refetch on login/logout: level-gated links (s151) depend on the viewer.
    publicAPI.getNavPages().then(r => setAllPages(r.data || [])).catch(() => {});
  }, [user?.member_id, member?.member_id]);

  // Scope footer pages to the active mini-site (same logic as the navbar):
  // on Personal Brand show the active personality's pages + "All Templates"
  // pages; on other themes show only universal pages.
  const active = isPB ? resolveActivePersonality(location.pathname, settings, allPages) : 'business';
  const scoped = isPB
    ? scopePagesForPersonality(allPages, active)
    : allPages.filter(p => !PB_CATS.has(p.category));
  const footerPages = scoped
    .filter(p => p.show_in_footer)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const isExternal = (url) => url && (url.startsWith('http://') || url.startsWith('https://'));
  const isAdmin = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
  return { settings, socialLinks, footerPages, isExternal, isAdmin };
}

function FooterContent({ settings, socialLinks, footerPages, isExternal, logoSrc, theme }) {
  const tt = useT();
  const { byKey } = useSocialCatalog();
  const isClassic = theme === 'classic';
  const isPersonalBrand = theme === 'personalbrand';
  const fontFamily = isClassic ? "'Playfair Display', serif"
    : isPersonalBrand ? "'Plus Jakarta Sans', 'Inter', sans-serif"
    : undefined;
  const API = BACKEND_URL;
  const brandName = tt(settings.brand_name) || 'Legacy';

  // Split footer pages into two columns
  const half = Math.ceil(footerPages.length / 2);
  const col1 = footerPages.slice(0, half);
  const col2 = footerPages.slice(half);

  const renderPageLink = (page) => {
    if (isExternal(page.url) || page.open_in_new_tab) {
      return (
        <a href={isExternal(page.url) ? page.url : (page.url || '/')} target="_blank" rel="noreferrer" className="text-white/50 text-sm hover:text-white transition-colors flex items-center gap-2 py-2 md:py-0">
          <ArrowRight className="w-3 h-3 flex-shrink-0" /> {page.title}
        </a>
      );
    }
    return (
      <Link to={page.url || `/page/${page.id}`} className="text-white/50 text-sm hover:text-white transition-colors flex items-center gap-2 py-2 md:py-0">
        <ArrowRight className="w-3 h-3 flex-shrink-0" /> {page.title}
      </Link>
    );
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Column 1: Logo + Description */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              {logoSrc ? (
                <img src={logoSrc} alt={brandName} className="h-10 w-auto object-contain" data-testid="footer-logo-img" />
              ) : (
                <>
                  <div className="w-9 h-9 flex items-center justify-center" style={{ backgroundColor: 'var(--color-accent, #0D9488)', borderRadius: isClassic ? 0 : '0.25rem' }}>
                    <span className="text-white font-bold text-sm" style={{ fontFamily: fontFamily || 'Playfair Display, serif' }}>{brandName[0]}</span>
                  </div>
                  <span className="text-lg font-bold" style={{ fontFamily: fontFamily || 'Playfair Display, serif' }}>{brandName}</span>
                </>
              )}
            </div>
            <p className="text-white/50 text-sm leading-relaxed" style={fontFamily ? { fontFamily } : {}}>
              {tt(settings.footer_description) || 'Strategic consulting for businesses seeking sustainable growth and lasting impact.'}
            </p>
          </div>

          {/* Columns 2-3: Site Map (two columns from CMS pages) */}
          <div className="sm:col-span-2 lg:col-span-2">
            <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-5" style={fontFamily ? { fontFamily } : {}}>Site Map</h4>
            {footerPages.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-8 gap-y-2.5" data-testid="footer-sitemap">
                <ul className="space-y-0.5 md:space-y-2.5">
                  {col1.map(page => <li key={page.id}>{renderPageLink(page)}</li>)}
                </ul>
                <ul className="space-y-0.5 md:space-y-2.5">
                  {col2.map(page => <li key={page.id}>{renderPageLink(page)}</li>)}
                </ul>
              </div>
            ) : (
              <p className="text-white/30 text-sm">No pages configured for footer.</p>
            )}
          </div>

          {/* Column 4: Connect (Social + Email) */}
          <div>
            <h4 className="text-white text-sm font-bold uppercase tracking-wider mb-5" style={fontFamily ? { fontFamily } : {}}>Connect</h4>
            {/* Grid, not a flex row: with `flex` the icons SHRINK (default
                flex-shrink) — 14 active networks in this column squeezed the
                32px circles into slivers packed against each other. `auto-fill`
                lets the column decide how many fit (6 here) and rows grow as
                needed; `max-w` stops them stretching into one long line when the
                footer collapses to a single column on mobile. Same approach as
                the KMS footer (D-2026-79). */}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(44px,1fr))] md:grid-cols-[repeat(auto-fill,minmax(32px,1fr))] gap-2 md:gap-1.5 mb-5 max-w-[320px] md:max-w-[228px]">
              {socialLinks.map(link => {
                const key = link.key || resolveKey(byKey, link) || '';
                const entry = byKey[key];
                return (
                  <a key={link.id} href={link.url} target="_blank" rel="noreferrer"
                    aria-label={link.platform || (entry && entry.label) || 'Social link'}
                    title={link.platform || (entry && entry.label) || ''}
                    className="aspect-square rounded-full border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/50 transition-colors"
                    data-testid={`footer-social-${key || link.icon || 'link'}`}>
                    <SocialIcon svg={entry && entry.svg} size={14} />
                  </a>
                );
              })}
            </div>
            <p className="text-white/50 text-sm mb-3">{tt(settings.footer_newsletter_text) || 'Get the latest insights delivered to your inbox.'}</p>
            <div className="flex">
              <input type="email" placeholder={tt(settings.footer_newsletter_placeholder) || 'Email address'}
                className="flex-1 bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 rounded-l-md placeholder:text-white/30 focus:outline-none focus:border-white/30"
                data-testid="footer-email-input" />
              <button className="px-4 py-2.5 rounded-r-md text-white text-sm font-medium flex items-center gap-1"
                style={{ backgroundColor: 'var(--color-accent, #0D9488)' }}
                data-testid="footer-subscribe-btn">
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-4">
        <p className="text-center text-white/30 text-xs" style={fontFamily ? { fontFamily } : {}}>
          {tt(settings.footer_copyright) || 'Legacy Consulting - All rights reserved.'}
        </p>
      </div>
    </>
  );
}

function DefaultFooter() {
  const { settings, socialLinks, footerPages, isExternal, isAdmin } = useFooterData();
  if (isAdmin) return null;
  const API = BACKEND_URL;
  const logoOff = settings.logo_off;
  const logoSrc = logoOff ? (logoOff.startsWith('/api') ? `${API}${logoOff}` : logoOff) : null;

  return (
    <footer style={{ backgroundColor: 'var(--color-footer-bg, #1a2332)', color: 'var(--color-footer-text, #FFFFFF)' }} data-testid="site-footer">
      <FooterContent settings={settings} socialLinks={socialLinks} footerPages={footerPages} isExternal={isExternal} logoSrc={logoSrc} theme="default" />
    </footer>
  );
}

// «Moderno» footer (s139, approved by Anthony: F1 "Graphite Editorial"):
// graphite band; logo + the brand tagline in large serif on top; three columns
// (about = footer description, site map = pages marked for the footer,
// connect = social links + the newsletter field as before); copyright bar.
// Same CMS data as the other footers; colours from theme tokens + graphite.
function ModernFooter() {
  const { settings, socialLinks, footerPages, isExternal, isAdmin } = useFooterData();
  const tt = useT();
  const { byKey } = useSocialCatalog();
  if (isAdmin) return null;
  const API = BACKEND_URL;
  const logoOff = settings.logo_off || settings.logo_on_1 || settings.logo_on;
  const logoSrc = logoOff ? (logoOff.startsWith('/api') ? `${API}${logoOff}` : logoOff) : null;
  const brandName = tt(settings.brand_name) || '';
  const tagline = tt(settings.tagline);
  const tWords = String(tagline || '').trim().split(/\s+/).filter(Boolean);
  const tLast = tWords.length > 1 ? tWords.pop() : null;
  const SERIF = "'Playfair Display', Georgia, serif";
  const muted = 'rgba(244,241,234,.62)';
  const head = 'text-[11px] font-bold uppercase mb-5';
  const pageLink = (page) => {
    const cls = 'inline-flex items-center min-h-[44px] md:min-h-0 text-sm transition-colors duration-200 hover:text-white';
    const style = { color: 'rgba(244,241,234,.82)' };
    return (isExternal(page.url) || page.open_in_new_tab)
      ? <a href={isExternal(page.url) ? page.url : (page.url || '/')} target="_blank" rel="noreferrer" className={cls} style={style}>{page.title}</a>
      : <Link to={page.url || `/page/${page.id}`} className={cls} style={style}>{page.title}</Link>;
  };

  return (
    <footer data-testid="site-footer" style={{ background: '#16171a', color: '#f4f1ea' }}>
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] pt-16 lg:pt-20 pb-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 pb-10 border-b" style={{ borderColor: 'rgba(255,255,255,.12)' }}>
          {logoSrc
            ? <img src={logoSrc} alt={brandName} className="h-10 w-auto object-contain self-start" data-testid="footer-logo-img" />
            : <span className="text-2xl font-bold" style={{ fontFamily: SERIF }}>{brandName}</span>}
          {tagline && (
            <p className="m-0 md:text-right" style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 500, fontSize: 'clamp(1.6rem, 3vw, 2.6rem)', lineHeight: 1.1 }} data-testid="footer-tagline">
              {tWords.join(' ')}{tLast && <> <span style={{ color: 'var(--color-accent, #ff9c1b)' }}>{tLast}</span></>}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1.2fr)_minmax(0,1fr)] gap-10 py-12">
          <div className="col-span-2 lg:col-span-1">
            <h4 className={head} style={{ letterSpacing: '.16em', color: muted }}>About</h4>
            {tt(settings.footer_description) && <p className="text-sm leading-relaxed max-w-md" style={{ color: muted }}>{tt(settings.footer_description)}</p>}
          </div>
          <div>
            <h4 className={head} style={{ letterSpacing: '.16em', color: muted }}>Site map</h4>
            {footerPages.length > 0 && (
              <ul className="m-0 p-0 list-none grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0 md:gap-y-3" data-testid="footer-sitemap">
                {footerPages.map(page => <li key={page.id}>{pageLink(page)}</li>)}
              </ul>
            )}
          </div>
          <div>
            <h4 className={head} style={{ letterSpacing: '.16em', color: muted }}>Connect</h4>
            {socialLinks.length > 0 && (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(36px,1fr))] gap-2 mb-6 max-w-[240px]">
                {socialLinks.map(link => {
                  const key = link.key || resolveKey(byKey, link) || '';
                  const entry = byKey[key];
                  return (
                    <a key={link.id || link.url} href={link.url} target="_blank" rel="noopener noreferrer"
                      aria-label={link.platform || (entry && entry.label) || 'Social link'} title={link.platform || (entry && entry.label) || ''}
                      className="aspect-square rounded-full border flex items-center justify-center transition-colors duration-200 hover:text-white"
                      style={{ borderColor: 'color-mix(in srgb, var(--color-accent, #ff9c1b) 45%, transparent)', color: 'var(--color-accent, #ff9c1b)' }}
                      data-testid={`footer-social-${key || link.icon || 'link'}`}>
                      <SocialIcon svg={entry && entry.svg} size={14} />
                    </a>
                  );
                })}
              </div>
            )}
            {tt(settings.footer_newsletter_text) && <p className="text-sm mb-3" style={{ color: muted }}>{tt(settings.footer_newsletter_text)}</p>}
            <div className="flex max-w-[280px]">
              <input type="email" placeholder={tt(settings.footer_newsletter_placeholder) || 'Email address'}
                className="flex-1 min-w-0 bg-transparent border text-sm px-3 py-2.5 placeholder:text-white/35 focus:outline-none"
                style={{ borderColor: 'rgba(255,255,255,.18)', color: '#f4f1ea' }} data-testid="footer-email-input" />
              <button className="px-4 py-2.5 text-sm font-semibold flex items-center" style={{ background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #0f172a)' }} aria-label="Subscribe" data-testid="footer-subscribe-btn">
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {tt(settings.footer_copyright) && (
          <div className="pt-6 border-t text-xs" style={{ borderColor: 'rgba(255,255,255,.12)', color: 'rgba(244,241,234,.5)' }} data-testid="footer-copyright">
            {tt(settings.footer_copyright)}
          </div>
        )}
      </div>
    </footer>
  );
}

function ClassicFooter() {
  const { settings, socialLinks, footerPages, isExternal, isAdmin } = useFooterData();
  if (isAdmin) return null;
  const API = BACKEND_URL;
  const logoOff = settings.logo_off;
  const logoSrc = logoOff ? (logoOff.startsWith('/api') ? `${API}${logoOff}` : logoOff) : null;

  return (
    <footer data-testid="site-footer">
      <div style={{ backgroundColor: 'var(--color-primary, #1a2332)' }}>
        <FooterContent settings={settings} socialLinks={socialLinks} footerPages={footerPages} isExternal={isExternal} logoSrc={logoSrc} theme="classic" />
      </div>
    </footer>
  );
}

function PersonalBrandFooter() {
  const { settings, socialLinks, footerPages, isExternal, isAdmin } = useFooterData();
  if (isAdmin) return null;
  const API = BACKEND_URL;
  const logoOff = settings.logo_off;
  const logoSrc = logoOff ? (logoOff.startsWith('/api') ? `${API}${logoOff}` : logoOff) : null;

  return (
    <footer
      data-testid="site-footer"
      style={{
        backgroundColor: 'var(--color-footer-bg, #111111)',
        color: 'var(--color-footer-text, #ffffff)',
        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
      }}
    >
      <FooterContent settings={settings} socialLinks={socialLinks} footerPages={footerPages} isExternal={isExternal} logoSrc={logoSrc} theme="personalbrand" />
    </footer>
  );
}

// ─── Private Community Pro footer (Option A: newsletter band + columns) ─────
// Dark PCP footer: a prominent newsletter band on top, then Brand + Site Map
// (CMS footer pages) + Connect (social) columns, then a copyright bar. Reuses
// the real CMS data (logo, description, footer pages, social, copyright).
function PcpFooter() {
  const { settings, socialLinks, footerPages, isExternal, isAdmin } = useFooterData();
  const tt = useT();
  const { byKey } = useSocialCatalog();
  if (isAdmin) return null;
  const API = BACKEND_URL;
  const logoOff = settings.logo_off;
  const logoSrc = logoOff ? (logoOff.startsWith('/api') ? `${API}${logoOff}` : logoOff) : null;
  const brandName = tt(settings.brand_name) || 'Legacy';
  const accent = 'var(--color-accent)';
  const nlText = tt(settings.footer_newsletter_text) || 'Get the latest insights delivered to your inbox.';
  const nlPlaceholder = tt(settings.footer_newsletter_placeholder) || 'Email address';

  const half = Math.ceil(footerPages.length / 2);
  const col1 = footerPages.slice(0, half);
  const col2 = footerPages.slice(half);
  const pageHref = (page) => (isExternal(page.url) ? page.url : (page.url || `/page/${page.id}`));
  const linkStyle = { display: 'block', fontSize: 14, padding: '5px 0', color: 'rgba(255,255,255,0.55)', textDecoration: 'none' };
  const renderLink = (page) => {
    const inner = <span className="pcp-flink">{page.title}</span>;
    if (isExternal(page.url) || page.open_in_new_tab) {
      return <a key={page.id} href={pageHref(page)} target="_blank" rel="noreferrer" style={linkStyle} className="pcp-flink-a">{inner}</a>;
    }
    return <Link key={page.id} to={pageHref(page)} style={linkStyle} className="pcp-flink-a">{inner}</Link>;
  };
  const h5Style = { margin: '0 0 16px', fontSize: 12, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: 'rgba(255,255,255,0.4)' };

  return (
    <footer style={{ backgroundColor: 'var(--color-footer-bg, #0f172a)', color: '#ffffff', fontFamily: "'Sora', sans-serif", position: 'relative', overflow: 'hidden' }} data-testid="site-footer">
      <div aria-hidden="true" style={{ position: 'absolute', top: -180, right: -140, width: 480, height: 480, borderRadius: 999, background: 'radial-gradient(circle, color-mix(in srgb, var(--color-accent) 12%, transparent), color-mix(in srgb, var(--color-accent) 0%, transparent) 70%)', pointerEvents: 'none' }} />

      {/* newsletter band */}
      <div style={{ position: 'relative', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '40px 24px' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-8 flex-wrap px-2 md:px-8">
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: accent, marginBottom: 8 }}>Newsletter</div>
            <p style={{ margin: 0, fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 22, letterSpacing: '-0.01em', color: '#fff', maxWidth: 440 }}>{nlText}</p>
          </div>
          <div className="flex items-center gap-2.5">
            <input type="email" placeholder={nlPlaceholder} data-testid="footer-email-input" style={{ width: 300, maxWidth: '60vw', height: 48, padding: '0 16px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: '#fff', fontSize: 14, fontFamily: "'Sora', sans-serif" }} />
            <button data-testid="footer-subscribe-btn" className="pcpb pcpb--green" data-contrast="dark" style={{ height: 48, fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Subscribe <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>

      {/* columns */}
      <div style={{ position: 'relative', padding: '56px 24px 40px' }}>
        <div className="max-w-7xl mx-auto px-2 md:px-8 grid grid-cols-1 md:grid-cols-[2fr,1.6fr,1fr] gap-10">
          {/* brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              {logoSrc
                ? <img src={logoSrc} alt={brandName} className="h-10 w-auto object-contain" data-testid="footer-logo-img" />
                : <>
                    <span className="inline-flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 9, background: 'color-mix(in srgb, var(--color-accent) 14%, transparent)' }}>
                      {settings.favicon
                        ? <img src={settings.favicon.startsWith('/api') ? `${API}${settings.favicon}` : settings.favicon} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                        : <span style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, fontSize: 16, color: accent }}>{brandName[0]}</span>}
                    </span>
                    <span style={{ fontFamily: "'Sora', sans-serif", fontSize: 19, fontWeight: 700, color: '#fff' }}>{brandName}</span>
                  </>}
            </div>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: 'rgba(255,255,255,0.5)', maxWidth: 400 }}>{tt(settings.footer_description) || ''}</p>
          </div>

          {/* site map */}
          <div>
            <h5 style={h5Style}>Site Map</h5>
            {footerPages.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-8" data-testid="footer-sitemap">
                <div>{col1.map(renderLink)}</div>
                <div>{col2.map(renderLink)}</div>
              </div>
            ) : <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>No pages configured for footer.</p>}
          </div>

          {/* connect */}
          <div>
            <h5 style={h5Style}>Connect</h5>
            <div className="flex flex-wrap gap-2.5">
              {socialLinks.map(link => {
                const key = link.key || resolveKey(byKey, link) || '';
                const entry = byKey[key];
                return (
                  <a key={link.id} href={link.url} target="_blank" rel="noreferrer" aria-label={link.platform || (entry && entry.label) || 'Social link'} title={link.platform || (entry && entry.label) || ''}
                    className="pcp-social inline-flex items-center justify-center" style={{ width: 38, height: 38, borderRadius: 999, border: '1px solid rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.6)' }}
                    data-testid={`footer-social-${key || link.icon || 'link'}`}>
                    <SocialIcon svg={entry && entry.svg} size={15} />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* bottom bar */}
      <div style={{ position: 'relative', borderTop: '1px solid rgba(255,255,255,0.08)', padding: '20px 24px' }}>
        <div className="max-w-7xl mx-auto px-2 md:px-8 flex items-center justify-between gap-5 flex-wrap">
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>{tt(settings.footer_copyright) || `© ${brandName} - All rights reserved.`}</span>
          <span className="inline-flex items-center gap-2" style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}><span style={{ width: 7, height: 7, borderRadius: 999, background: accent }} /> {tt(settings.tagline) || 'Private members community'}</span>
        </div>
      </div>
    </footer>
  );
}

export default function Footer() {
  const settings = useSettings();
  const theme = settings.active_theme || 'default';
  if (theme === 'privatecommunity') return <PcpFooter />;
  if (theme === 'modern') return <ModernFooter />;
  if (theme === 'classic') return <ClassicFooter />;
  if (theme === 'personalbrand') return <PersonalBrandFooter />;
  return <DefaultFooter />;
}
